import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SparklesIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { toast } from '@ems/ui/components/sonner'

import {
  approveApplication,
  assignBooths,
  createBoothInvoice,
  markApplicationPaid,
  verifyApplication,
  type Application,
  type Booth,
  type BoothType,
} from '#admin/features/exhibitor/api.ts'
import {
  bodyOf,
  COMPANY_KINDS,
  createCompany,
  EMPTY_DRAFT,
  setCompanyKinds,
  type Company,
  type CompanyKind,
} from '#admin/features/exhibitor/companies-api.ts'
import {
  claimPass,
  invoiceOrder,
  issuePasses,
  openApplication,
  submitApplication,
  type BadgeType,
  type PassHolder,
} from '#admin/features/exhibitor/onboarding/api.ts'
import { CompanySection } from '#admin/features/exhibitor/onboarding/company-section.tsx'
import {
  deskCurrencies,
  deskEstimate,
  deskIssues,
  emptyDraft,
  namedStaff,
  passQuota,
  standsIn,
  type DeskDraft,
  type Stand,
  type StaffRow,
} from '#admin/features/exhibitor/onboarding/draft.ts'
import {
  DeskStepError,
  NO_PROGRESS,
  runDesk,
  stepLabel,
  stepsOf,
  type DeskApi,
  type DeskPlan,
  type DeskProgress,
} from '#admin/features/exhibitor/onboarding/run.ts'
import { ServicesSection } from '#admin/features/exhibitor/onboarding/services-section.tsx'
import { SettlementSection } from '#admin/features/exhibitor/onboarding/settlement-section.tsx'
import { StaffPassesSection } from '#admin/features/exhibitor/onboarding/staff-passes-section.tsx'
import { StandPickerSection } from '#admin/features/exhibitor/onboarding/stand-picker-section.tsx'
import {
  StickySummary,
  type RunState,
} from '#admin/features/exhibitor/onboarding/sticky-summary.tsx'
import { SuccessCard } from '#admin/features/exhibitor/onboarding/success-card.tsx'
import type { CatalogItem } from '#admin/features/exhibitor/service-items-api.ts'
import {
  basketOf,
  createOrder,
  forSaleIn,
  submitServiceOrder,
} from '#admin/features/exhibitor/service-orders-api.ts'
import { cleanErrorMessage } from '#admin/lib/error-message.ts'

/** The stand desk, or the sponsor desk: the same steps, with the sponsor's own words. */
export type DeskVariant = 'exhibition' | 'sponsor'

/**
 * What a new company is. A sponsor is an exhibitor too: the server books stands for exhibitors.
 */
const KINDS: Record<DeskVariant, readonly CompanyKind[]> = {
  exhibition: ['exhibitor'],
  sponsor: ['exhibitor', 'sponsor'],
}

const DESK_API: DeskApi = {
  createCompany,
  setCompanyKinds,
  openApplication,
  assignBooths,
  submitApplication,
  verifyApplication,
  approveApplication,
  createOrder,
  submitServiceOrder,
  invoiceOrder,
  markApplicationPaid,
  createBoothInvoice,
  claimPass,
  issuePasses,
}

/**
 * The onboarding desk: one page that books a company's stands, orders its services, settles the
 * payment and issues its staff badges. The page sends the requests one at a time (`run.ts`).
 * A failed step keeps what is done, and the next try starts at that step.
 */
export function MasterOnboardingForm({
  companies,
  applications,
  booths,
  boothTypes,
  catalog,
  badgeTypes,
  editionCurrency,
  eventName,
  variant = 'exhibition',
}: {
  companies: readonly Company[]
  applications: readonly Application[]
  booths: readonly Booth[]
  boothTypes: readonly BoothType[]
  catalog: readonly CatalogItem[]
  /** Empty when the edition cannot issue badges. */
  badgeTypes: readonly BadgeType[]
  editionCurrency: string
  eventName: string
  variant?: DeskVariant
}) {
  const sponsor = variant === 'sponsor'
  const queryClient = useQueryClient()
  const currencies = deskCurrencies(boothTypes, editionCurrency)
  const [draft, setDraft] = useState<DeskDraft>(() => emptyDraft(currencies[0] ?? editionCurrency))
  const [progress, setProgress] = useState<DeskProgress>(NO_PROGRESS)
  const [run, setRun] = useState<RunState | { phase: 'done' }>({ phase: 'editing' })
  // The stands at the moment the run started. The free list no longer has them once they are held.
  const [booked, setBooked] = useState<readonly Stand[]>([])

  const change = (changes: Partial<DeskDraft>) =>
    setDraft((current) => ({ ...current, ...changes }))

  const stands = standsIn(booths, boothTypes, draft.currency)
  const picked = progress.boothsAssigned
    ? booked
    : stands.filter((stand) => draft.boothIds.includes(stand.id))
  const services = forSaleIn(
    catalog.filter((item) => item.isActive),
    draft.currency,
  )
  // A sponsor's people get full access, so the sponsor desk starts on the convention pass.
  const conventionPass = badgeTypes.find((type) => type.category !== 'exhibitor_pass')
  const passTypeId =
    badgeTypes.length === 1
      ? (badgeTypes[0]?.id ?? '')
      : draft.passTypeId === '' && sponsor
        ? (conventionPass?.id ?? '')
        : draft.passTypeId
  const issues = deskIssues({ ...draft, passTypeId }, picked, badgeTypes.length > 0)
  const estimate = deskEstimate(picked, catalog, draft)
  const plan = planOf(draft, picked, services, passTypeId, badgeTypes.length > 0, {
    kinds: KINDS[variant],
    company: companies.find((company) => company.id === draft.companyId),
  })
  const steps = stepsOf(plan).map((step) => ({ step, label: stepLabel(step, plan) }))
  const takenCompanies = new Set(
    applications
      .filter((a) => a.companyId !== null && a.status !== 'rejected' && a.status !== 'canceled')
      .map((a) => a.companyId ?? ''),
  )

  const running = run.phase === 'running'
  const status = progress.application?.status
  const settled = status === 'payment_pending' || status === 'payment_completed'

  const complete = async () => {
    setBooked(picked)
    setRun({ phase: 'running', step: stepsOf(plan)[0] ?? 'application' })
    try {
      const done = await runDesk(plan, progress, DESK_API, (next, step) => {
        setProgress(next)
        setRun({ phase: 'running', step })
      })
      setProgress(done)
      setRun({ phase: 'done' })
      toast.success(`${done.application?.companyName ?? 'The exhibitor'} is registered.`)
    } catch (error) {
      if (!(error instanceof DeskStepError)) throw error
      setProgress(error.progress)
      setRun({ phase: 'failed', step: error.step, message: messageOf(error.cause) })
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['exhibitor'] })
    }
  }

  const reset = () => {
    setDraft(emptyDraft(currencies[0] ?? editionCurrency))
    setProgress(NO_PROGRESS)
    setBooked([])
    setRun({ phase: 'editing' })
  }

  if (run.phase === 'done' && progress.application !== null) {
    return (
      <SuccessCard
        application={progress.application}
        stands={booked}
        serviceInvoiceId={progress.serviceInvoiceId}
        registrationId={progress.registrationId}
        badgeTypeName={badgeTypes.find((type) => type.id === passTypeId)?.name ?? null}
        eventName={eventName}
        onReset={reset}
      />
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="flex min-w-0 flex-col gap-6">
        {sponsor ? (
          <Alert className="border-amber-500/40 bg-amber-500/5">
            <SparklesIcon className="text-amber-600 dark:text-amber-400" />
            <AlertTitle>Sponsor package onboarding</AlertTitle>
            <AlertDescription>
              Registering an official event sponsor. This flow books their allocated stand,
              services, and convention passes, automatically recording the company as both a sponsor
              and an exhibitor.
            </AlertDescription>
          </Alert>
        ) : null}
        <CompanySection
          variant={variant}
          draft={draft}
          onChange={change}
          issues={issues}
          companies={companies}
          booked={takenCompanies}
          locked={running || progress.companyId !== null || progress.application !== null}
        />
        <StandPickerSection
          {...(sponsor
            ? {
                title: 'Sponsor stand allocation',
                description: 'Select the stand(s) allocated for this sponsor package.',
              }
            : {})}
          stands={progress.boothsAssigned ? booked : stands}
          chosen={new Set(picked.map((stand) => stand.id))}
          onToggle={(id) =>
            change({
              boothIds: draft.boothIds.includes(id)
                ? draft.boothIds.filter((other) => other !== id)
                : [...draft.boothIds, id],
            })
          }
          currency={draft.currency}
          currencies={currencies}
          onCurrency={(currency) => change({ currency, boothIds: [], quantities: {} })}
          currencyLocked={running || progress.application !== null}
          error={issues['stands']}
          locked={running || progress.boothsAssigned}
        />
        <ServicesSection
          services={services}
          currency={draft.currency}
          quantities={draft.quantities}
          onQuantity={(id, quantity) =>
            change({ quantities: { ...draft.quantities, [id]: quantity } })
          }
          error={issues['services']}
          locked={running || progress.serviceOrderId !== null}
        />
        <StaffPassesSection
          staff={draft.staff}
          onStaff={(staff) => change({ staff })}
          quota={passQuota(picked)}
          badgeTypes={badgeTypes}
          passTypeId={passTypeId}
          onPassType={(id) => change({ passTypeId: id })}
          settlement={draft.settlement}
          issues={issues}
          locked={running || progress.claimed > 0}
        />
        <SettlementSection
          settlement={draft.settlement}
          onSettlement={(settlement) => change({ settlement })}
          note={draft.paymentNote}
          onNote={(paymentNote) => change({ paymentNote })}
          locked={running || settled}
        />
      </div>

      <StickySummary
        title={sponsor ? 'Sponsor package summary' : 'Order summary'}
        estimate={estimate}
        currency={draft.currency}
        settlement={draft.settlement}
        steps={steps}
        run={run.phase === 'done' ? { phase: 'editing' } : run}
        problems={Object.keys(issues).length}
        onComplete={() => void complete()}
      />
    </div>
  )
}

/** What the run sends, from the form. */
function planOf(
  draft: DeskDraft,
  picked: readonly Stand[],
  services: readonly CatalogItem[],
  passTypeId: string,
  badgesAvailable: boolean,
  kinds: { kinds: readonly CompanyKind[]; company: Company | undefined },
): DeskPlan {
  const phone = draft.contactPhone.trim()
  const staff =
    draft.settlement === 'paid' && badgesAvailable ? namedStaff(draft.staff).map(holderOf) : []
  const note = draft.paymentNote.trim()
  return {
    company:
      draft.companyMode === 'existing'
        ? companyOf(draft.companyId, kinds)
        : {
            body: bodyOf({
              ...EMPTY_DRAFT,
              name: draft.companyName,
              industrySector: draft.industrySector,
              kinds: [...kinds.kinds],
            }),
          },
    application: {
      contactName: draft.contactName.trim(),
      contactEmail: draft.contactEmail.trim(),
      ...(phone === '' ? {} : { contactPhone: phone }),
      boothTypeId: picked[0]?.type.id ?? '',
      currency: draft.currency,
    },
    boothIds: picked.map((stand) => stand.id),
    // Only the services for sale in this currency. A quantity left from another one is dropped.
    services: basketOf(draft.quantities).items.filter((line) =>
      services.some((item) => item.id === line.serviceItemId),
    ),
    settlement:
      draft.settlement === 'paid'
        ? { kind: 'paid', note: note === '' ? 'Paid at the onboarding desk.' : note }
        : { kind: 'invoice' },
    staff,
    passTypeId: staff.length > 0 ? passTypeId : null,
  }
}

/** A known company, with the kinds it lacks added. Only the sponsor desk adds a kind. */
function companyOf(
  id: string,
  { kinds, company }: { kinds: readonly CompanyKind[]; company: Company | undefined },
): DeskPlan['company'] {
  const has = COMPANY_KINDS.filter((kind) => company?.kinds.includes(kind) === true)
  const missing = kinds.filter((kind) => !has.includes(kind))
  return company === undefined || missing.length === 0
    ? { id }
    : { id, kinds: [...has, ...missing] }
}

function holderOf(row: StaffRow): PassHolder {
  const holder: PassHolder = { holderName: row.name.trim(), email: row.email.trim() }
  if (row.title.trim() !== '') holder.jobTitle = row.title.trim()
  return holder
}

/** The server's message for a refusal. A company that has an application is named as such. */
function messageOf(error: unknown): string {
  if (error instanceof ApiClientError && error.detail?.['reason'] === 'company_has_application') {
    return 'This company already has an application. Open it from the Exhibitors list.'
  }
  return error instanceof Error
    ? cleanErrorMessage(error.message)
    : 'The request did not complete. Check your connection and try again.'
}
